import { ProjectRenderer } from '../ProjectRenderer';
import { RenderUtils } from '../utils/RenderUtils';
import { AssetsLib } from '../utils/AssetsLib';
import {
	Box3,
	Euler,
	Mesh,
	MeshPhysicalMaterial,
	MeshStandardMaterial,
	Vector3,
	Vector3Tuple,
} from 'three';
import { DraggableObject } from './DraggableObject';
import {
	UserDataProp,
	UserDataObjectType,
	SerializedDraggableObject,
	BarType,
} from '../types';
import { ChangeLevelDirection, RotationDirection, UUID } from '@/types/types';
import { ModulesGrid } from '../utils/ModulesGrid';
import { Asset } from '@/api/AssetApi';
import { CameraType } from '@/types/creator';
import { sample } from 'lodash';
import RENDERER_CONFIG from '@/configs/rendererConfig';

const { PRODUCT_MODULE: PROJECT_MODULE } = RENDERER_CONFIG;

const NORMALS = [
	new Vector3(1, 0, 0),
	new Vector3(-1, 0, 0),
	new Vector3(0, 1, 0),
	new Vector3(0, -1, 0),
	new Vector3(0, 0, 1),
	new Vector3(0, 0, -1),
];

export interface SnapPoint {
	position: Vector3;
	normal: Vector3;
}

export interface SerializedProductModule extends SerializedDraggableObject {
	blockLayout: Vector3Tuple[];
	customColors: [string, string][];
	barsColors: Record<BarType, string>;
	pivotBlock?: Vector3Tuple;
}

export class ProductModule extends DraggableObject {
	static readonly LAYER = PROJECT_MODULE.LAYER;

	private modulesGrid: ModulesGrid;
	private assetsLib = AssetsLib.getInstance();
	private blockPositionsSet: Set<string> = new Set();
	private blockLayout: Vector3Tuple[];
	private asset: Asset;
	private barsColors: Record<BarType, string> = {
		[BarType.HORIZONTAL]: '',
		[BarType.VERTICAL]: '',
	};
	private customColors: Map<string, string> = new Map();
	private savedState: {
		blockLayout: Vector3Tuple[];
		position: Vector3Tuple;
		pivotBlock: Vector3Tuple;
		rotation: Euler;
		level: number;
	} | null = null;
	private pivotBlock: Vector3Tuple;
	private disposed = false;
	private readyPromise: Promise<void>;
	localBlockPositions: Vector3[] = [];

	constructor(
		renderer: ProjectRenderer,
		id: UUID,
		asset: Asset,
		config?: SerializedProductModule
	) {
		super(renderer);

		this.modulesGrid = this.renderer.modulesManager.modulesGrid;

		this.id = id;
		this.blockLayout = asset.options.blockLayout;
		this.pivotBlock = this.choosePivotBlock(this.blockLayout);

		this.asset = asset;

		this.level = this.renderer.currentLevel;

		this.group.position.setY(this.level * ModulesGrid.BLOCK_HEIGHT);

		this.renderer.room.add(this.group);

		this.updateBlockPositions();

		this.readyPromise = this.initialize(config);
	}

	get ready() {
		return this.readyPromise;
	}

	private async initialize(config?: SerializedProductModule) {
		await this.loadAsset(this.asset.model!.path);
		if (this.disposed) return;

		if (!config) this.placeToNearestAvailablePosition();

		this.group.traverse((child) => {
			if (!RenderUtils.isMesh(child)) return;
			if (Array.isArray(child.material)) return;

			const copy = (child.material as MeshStandardMaterial).clone();

			copy.emissive.set(0x0000ff);
			copy.emissiveIntensity = 0;

			copy.transparent = true;

			copy.onBeforeCompile = (shader) => {
				shader.vertexShader = `
				    varying vec3 vWorldPosition;
				    ${shader.vertexShader}
				`.replace(
					'#include <worldpos_vertex>',
					`
							#include <worldpos_vertex>
							vWorldPosition = worldPosition.xyz;
				    `
				);

				shader.fragmentShader = `
				    varying vec3 vWorldPosition;
				    uniform float lowY;
				    uniform float highY;
				    uniform bool forceVisible;
				    ${shader.fragmentShader}
					`;

				shader.fragmentShader = shader.fragmentShader.replace(
					'#include <tonemapping_fragment>',
					`
							float yy = vWorldPosition.y;
							float grayScale = 0.0;

							if (forceVisible) {
								gl_FragColor.a = 1.0;
								grayScale = 0.0;
							} else if (yy < lowY) {
								gl_FragColor.a = 0.2;
								grayScale = 0.5;
							} else if (yy > highY) {
								gl_FragColor.a = 0.0;
								grayScale = 0.0;
							} else {
								gl_FragColor.a = 1.0;
								grayScale = 0.0;
							}

							float gray = dot(gl_FragColor.rgb, vec3(0.299, 0.587, 0.114));
							gl_FragColor.rgb = mix(gl_FragColor.rgb, vec3(gray), grayScale);


							#include <tonemapping_fragment>
				    `
				);

				shader.uniforms.lowY = {
					value: 0,
				};
				shader.uniforms.highY = {
					value: 100,
				};
				shader.uniforms.forceVisible = {
					value: false,
				};

				if (Array.isArray(child.material)) return;

				copy.userData[UserDataProp.LEVELS_SHADER] = shader.uniforms;
			};

			child.material = copy;
		});

		if (this.disposed) return;

		if (config) this.loadFromData(config);

		this.modulesGrid.registerModule(this);
		this.setLevelVisibility();
	}

	get position() {
		return this.group.position;
	}

	set position(value: Vector3) {
		this.group.position.copy(value);
		this.updateBox();
	}

	override set isDragging(value: boolean) {
		if (this._isDragging === value) return;
		this._isDragging = value;

		this.group.traverse((child) => {
			if (!RenderUtils.isMesh(child)) return;
			if (Array.isArray(child.material)) return;

			(child.material as MeshStandardMaterial).emissiveIntensity = value
				? 2
				: 0;
		});
	}

	protected override loadFromData<T extends SerializedDraggableObject>(
		data: T
	) {
		this.group.position.set(...data.position);
		this.group.rotation.y = data.rotation;
		this.level = Math.round(data.position[1] / ModulesGrid.BLOCK_HEIGHT);

		if ('blockLayout' in data) {
			this.blockLayout = data.blockLayout as Vector3Tuple[];
			this.updateBlockPositions();
			this.box.setFromObject(this.group);
		}

		if ('pivotBlock' in data && data.pivotBlock)
			this.pivotBlock = data.pivotBlock as Vector3Tuple;
		else this.pivotBlock = this.restorePivotBlockFromRotation(data.rotation);

		if ('customColors' in data) {
			this.customColors.clear();
			const customColors =
				data.customColors as SerializedProductModule['customColors'];

			for (const entry of customColors) {
				this.customColors.set(entry[0], entry[1]);
				const part = this.group.getObjectByName(entry[0]);
				if (part) this.setPartColor(part as Mesh, entry[1]);
			}
		}

		if ('barsColors' in data) {
			const barsColors =
				data.barsColors as SerializedProductModule['barsColors'];

			let barType: BarType;
			for (barType in barsColors) {
				const color = barsColors[barType];

				this.group.traverse((child) => {
					if (!RenderUtils.isMesh(child)) return;
					if (Array.isArray(child.material)) return;
					if (child.userData[UserDataProp.BAR_TYPE] !== barType) return;
					this.setBarsColor(child as Mesh, color);
				});
			}
		}
	}

	private restorePivotBlockFromRotation(rotation: number) {
		let turns = Math.round(rotation / DraggableObject.ROTATION_SNAP) % 4;
		if (turns < 0) turns += 4;

		let pivotBlock = this.choosePivotBlock(this.asset.options.blockLayout);

		for (let i = 0; i < turns; i++)
			pivotBlock = [pivotBlock[2], pivotBlock[1], -pivotBlock[0]];

		return pivotBlock;
	}

	private async loadAsset(src: string) {
		const model = await this.assetsLib.getModel(src);
		if (this.disposed) return;

		model.traverse((child) => {
			child.userData[UserDataProp.OBJECT_TYPE] =
				UserDataObjectType.PRODUCT_MODULE;
			child.userData[UserDataProp.OBJECT_ID] = this.id;
			child.layers.set(ProductModule.LAYER);
		});

		this.group.add(model);

		this.updateBox();
		if (import.meta.env.DEV) this.renderer.scene.add(this.boxHelper);
	}

	private updateBlockPositions() {
		this.localBlockPositions = this.blockLayout.map(
			(coords) => new Vector3(...coords)
		);

		this.blockPositionsSet.clear();
		for (const p of this.localBlockPositions) {
			this.blockPositionsSet.add(`${p.x},${p.y},${p.z}`);
		}
	}

	private choosePivotBlock(blockLayout: Vector3Tuple[]) {
		return blockLayout.reduce(
			(acc, curr) => {
				if (!acc) return curr;

				if (curr[0] < acc[0] || (curr[0] === acc[0] && curr[2] > acc[2]))
					return curr;

				return acc;
			},
			null as Vector3Tuple | null
		)!;
	}

	private blockToWorldPosition(block: Vector3Tuple) {
		return new Vector3(
			this.group.position.x + block[0] * ModulesGrid.GRID_UNIT,
			this.group.position.y + block[1] * ModulesGrid.BLOCK_HEIGHT,
			this.group.position.z + block[2] * ModulesGrid.GRID_UNIT
		);
	}

	private placeToNearestAvailablePosition() {
		const nearestPosition = this.modulesGrid.findNearestAvailablePosition(
			this,
			new Vector3(0, this.position.y, 0)
		);

		this.position = nearestPosition;
	}

	override rotate(direction: RotationDirection) {
		const angle =
			direction === RotationDirection.CLOCKWISE
				? -DraggableObject.ROTATION_SNAP
				: DraggableObject.ROTATION_SNAP;

		this.renderer.room.updateMatrixWorld(true);
		this.group.updateMatrixWorld(true);

		const pivotWorld = this.blockToWorldPosition(this.pivotBlock).clone();
		const pivotParentLocal = this.renderer.room.worldToLocal(
			pivotWorld.clone()
		);

		const offset = this.group.position.clone().sub(pivotParentLocal);

		offset.applyAxisAngle(new Vector3(0, 1, 0), angle);

		this.group.position.copy(pivotParentLocal).add(offset);

		this.group.rotateY(angle);

		switch (direction) {
			case RotationDirection.CLOCKWISE:
				this.blockLayout = this.blockLayout.map(([x, y, z]) => [-z, y, x]);
				this.pivotBlock = [
					-this.pivotBlock[2],
					this.pivotBlock[1],
					this.pivotBlock[0],
				];
				break;
			case RotationDirection.COUNTER_CLOCKWISE:
				this.blockLayout = this.blockLayout.map(([x, y, z]) => [z, y, -x]);
				this.pivotBlock = [
					this.pivotBlock[2],
					this.pivotBlock[1],
					-this.pivotBlock[0],
				];
				break;
		}

		this.updateBlockPositions();
		this.box.setFromObject(this.group);
	}

	setPartsRandomColors(colors: string[]) {
		this.group.traverse((child) => {
			if (!RenderUtils.isMesh(child)) return;
			if (Array.isArray(child.material)) return;
			if (!child.userData[UserDataProp.IS_COLORABLE]) return;
			if (child.userData[UserDataProp.BAR_TYPE]) return;

			const color = sample(colors)!;

			this.setPartColor(child, color);
		});
	}

	changeLevel(direction: ChangeLevelDirection) {
		const delta = direction === ChangeLevelDirection.UP ? 1 : -1;

		this.group.position.y += delta * ModulesGrid.BLOCK_HEIGHT;
		this.level += delta;

		this.box.setFromObject(this.group);
		this.setLevelVisibility();
	}

	checkIsAnyBlockOnCurrentLevel() {
		const moduleGridCoords = this.modulesGrid.getModuleGridCoords(this);

		return moduleGridCoords.some(
			(coords) => coords.y === this.renderer.currentLevel
		);
	}

	setLevelVisibility() {
		const currentLevel = this.renderer.currentLevel;

		const isAnyBlockOnCurrentLevel = this.checkIsAnyBlockOnCurrentLevel();

		this.group.traverse((child) => {
			if (!RenderUtils.isMesh(child)) return;
			if (Array.isArray(child.material)) return;

			const uniforms = child.material.userData[UserDataProp.LEVELS_SHADER];
			if (!uniforms) return;

			uniforms.forceVisible.value =
				isAnyBlockOnCurrentLevel ||
				this.renderer.cameraController.activeCamera === CameraType.PERSPECTIVE;
			uniforms.lowY.value = ModulesGrid.BLOCK_HEIGHT * currentLevel;
			uniforms.highY.value = ModulesGrid.BLOCK_HEIGHT * (currentLevel + 1);
		});
	}

	snapToGrid() {
		this.position = ModulesGrid.snapModulePositionToGrid(
			this.position,
			this.getBoundingBoxInGridUnits()
		);
	}

	save() {
		this.savedState = structuredClone({
			blockLayout: this.blockLayout,
			position: this.group.position.toArray(),
			pivotBlock: this.pivotBlock,
			rotation: this.group.rotation.clone(),
			level: this.level,
		});
	}

	restore() {
		if (!this.savedState) return;

		this.renderer.room.updateMatrixWorld(true);
		this.group.updateMatrixWorld(true);

		this.blockLayout = this.savedState.blockLayout;
		this.group.position.fromArray(this.savedState.position);
		this.pivotBlock = this.savedState.pivotBlock;
		this.group.rotation.copy(this.savedState.rotation);
		this.level = this.savedState.level;
		this.updateBlockPositions();
		this.box.setFromObject(this.group);

		this.savedState = null;
		this.setLevelVisibility();
	}

	setPartColor(part: Mesh, color: string) {
		this.customColors.set(part.name, color);

		const material = part.material as
			| MeshStandardMaterial
			| MeshPhysicalMaterial;

		if (material.type === 'MeshStandardMaterial') material.color.set(color);
		else if (material.type === 'MeshPhysicalMaterial') {
			material.color.set(color);
			(material as MeshPhysicalMaterial).specularColor.set(color);
			(material as MeshPhysicalMaterial).specularIntensity = 1;
		}
	}

	setBarsColor(bars: Mesh, color: string) {
		const barType = bars.userData[UserDataProp.BAR_TYPE] as BarType;

		this.barsColors[barType] = color;

		const material = bars.material as
			| MeshStandardMaterial
			| MeshPhysicalMaterial;

		if (material.type === 'MeshStandardMaterial') material.color.set(color);
		else if (material.type === 'MeshPhysicalMaterial') {
			material.color.set(color);
			(material as MeshPhysicalMaterial).specularColor.set(color);
			(material as MeshPhysicalMaterial).specularIntensity = 1;
		}
	}

	getBarColor(barType: BarType) {
		return this.barsColors[barType];
	}

	hasBarColor(barType: BarType) {
		return this.barsColors[barType] !== '';
	}

	setBarTypeColor(barType: BarType, color: string) {
		this.group.traverse((child) => {
			if (!RenderUtils.isMesh(child)) return;
			if (Array.isArray(child.material)) return;
			if (child.userData[UserDataProp.BAR_TYPE] !== barType) return;

			this.setBarsColor(child as Mesh, color);
		});
	}

	getBoundingBoxInGridUnits() {
		if (this.localBlockPositions.length === 0) return new Vector3(0, 0, 0);

		const box = new Box3();

		for (const localPos of this.localBlockPositions) {
			const pos = new Vector3(
				localPos.x * ModulesGrid.GRID_UNIT,
				localPos.y * ModulesGrid.BLOCK_HEIGHT,
				localPos.z * ModulesGrid.GRID_UNIT
			);
			box.expandByPoint(pos);
		}

		const size = new Vector3();
		box.getSize(size);

		const stepsX = Math.round(size.x / ModulesGrid.GRID_UNIT) + 1;
		const stepsY = Math.round(size.y / ModulesGrid.BLOCK_HEIGHT) + 1;
		const stepsZ = Math.round(size.z / ModulesGrid.GRID_UNIT) + 1;

		return new Vector3(stepsX, stepsY, stepsZ);
	}

	getSnapPoints(position = this.group.position) {
		const points: SnapPoint[] = [];
		const halfGrid = ModulesGrid.GRID_UNIT / 2;

		for (const localPos of this.localBlockPositions) {
			for (const normal of NORMALS) {
				const neighborPos = localPos.clone().add(normal);
				const neighborKey = `${neighborPos.x},${neighborPos.y},${neighborPos.z}`;

				if (!this.blockPositionsSet.has(neighborKey)) {
					const worldBlockCenter = new Vector3(
						position.x + localPos.x * ModulesGrid.GRID_UNIT,
						position.y + localPos.y * ModulesGrid.BLOCK_HEIGHT,
						position.z + localPos.z * ModulesGrid.GRID_UNIT
					);

					let offset = halfGrid;

					if (normal.y !== 0) offset = ModulesGrid.BLOCK_HEIGHT / 2;

					const snapPosition = worldBlockCenter
						.clone()
						.add(normal.clone().multiplyScalar(offset));

					points.push({
						position: snapPosition,
						normal: normal.clone(),
					});
				}
			}
		}
		return points;
	}

	dispose() {
		this.disposed = true;
		this.modulesGrid.unregisterModule(this);
		RenderUtils.disposeObject(this.group);
		if (this.boxHelper) RenderUtils.disposeObject(this.boxHelper);
	}

	export(): SerializedProductModule {
		return {
			id: this.id,
			asset: this.asset,
			blockLayout: this.blockLayout,
			position: this.group.position.toArray(),
			rotation: this.group.rotation.y,
			customColors: Array.from(this.customColors.entries()),
			barsColors: this.barsColors,
			pivotBlock: this.pivotBlock,
		};
	}
}
